import { useEffect } from "react";
import { Outlet, createFileRoute, useMatch } from "@tanstack/react-router";

import { registerWorkspaceHeader } from "../../../lib/nav";
import { WorkspaceGate } from "../../../shell/WorkspaceGate";
import { useProjectAddress } from "../../../shell/project";
import { useReportList } from "../../../workspaces/reports/project";
import { prefetchForProject } from "../../../lib/prefetch";

export const Route = createFileRoute("/_console/p/$org/$project/reports")({
  loader: ({ context: { queryClient }, params }) =>
    prefetchForProject(
      queryClient,
      params,
      Promise.all([
        import("../../../workspaces/reports/api"),
        import("../../../workspaces/reports/project"),
      ]),
      ([{ createReportsApi }, { reportListQuery }], projectId) =>
        void queryClient.prefetchQuery(
          reportListQuery(projectId, createReportsApi(projectId)),
        ),
    ),
  component: ReportsLayout,
});

/* Reports and the data model they read are two views of one page, a tab strip under the
 * top bar; a report's own page and its editor stand alone. The door's answer to the list
 * is the workspace's gate: every page here finds its report through it. The two tabbed
 * pages draw the list as a region of their own, so they draw while it is on its way; a
 * report's own page waits for it, because a name the list has not answered for is not a
 * missing report. */
function ReportsLayout() {
  const address = useProjectAddress();
  const list = useReportList();
  const onIndex = useMatch({ from: "/_console/p/$org/$project/reports/", shouldThrow: false }) !== undefined;
  const onModel = useMatch({ from: "/_console/p/$org/$project/reports/model", shouldThrow: false }) !== undefined;
  const tabs = onIndex || onModel;
  useEffect(() => {
    if (!tabs) return;
    return registerWorkspaceHeader({
      workspace: "projects",
      presentation: "tabs",
      items: [
        { to: "/p/$org/$project/reports", params: address, label: "Reports", icon: "report", end: true },
        { to: "/p/$org/$project/reports/model", params: address, label: "Data model", icon: "query" },
      ],
    });
  }, [address, tabs]);
  if (list.data === undefined)
    return <WorkspaceGate name="Reports" query={list} pending={tabs ? <Outlet /> : undefined} />;
  return <Outlet />;
}
